import { PageLayout, ParagraphSnapshot } from '../../../core/types/Typesetter';
import { mapParagraphIdsToIndices, findMinParagraphIndex, findParagraphIdWithMinIndex } from '../paragraph';
import { findParagraphSliceInPage } from '../paragraph';
import { findMinValueWithDefault } from '../utils';
import { generatePageCacheKey } from '../cache';

// Debug flag - set to false for production
const DEBUG_ENABLED = false;

// Production log verbosity - set to false to disable Phase3A logs
const PRODUCTION_LOGS_ENABLED = false;

/**
 * SpillFrontierCalculator - Utility for calculating spill frontiers in incremental pagination
 *
 * A spill frontier represents the cursor position where incremental pagination should resume
 * after a document edit. This calculator determines the optimal starting point by finding
 * the first affected paragraph and its position within the existing page layout.
 */
export class SpillFrontierCalculator {
  /**
   * Calculate SpillFrontier from affected paragraph IDs
   *
   * Finds the first page containing affected content to determine where incremental
   * pagination should resume. This is critical for efficient incremental updates
   * that avoid repaginating unchanged content.
   *
   * Strategy:
   * 1. Find paragraph metadata for affected IDs
   * 2. Find first affected paragraph (minimum para_ix)
   * 3. Find first page containing that paragraph
   * 4. Find paragraph slice in that page to get starting line
   * 5. Return SpillFrontier pointing to that paragraph's first line on that page
   *
   * @param affectedParagraphIds - Array of paragraph IDs that were modified
   * @param documentParagraphs - Array of paragraph metadata with id, graphemeStart, graphemeEnd, version
   * @param paragraphToPageMap - Map of paragraph_id to array of page indices containing that paragraph
   * @param currentDocHash - Current document hash for cache key generation
   * @param pageCache - Map of cache keys to PageLayout objects
   * @returns SpillFrontier cursor or null if calculation fails
   */
  static calculateSpillFrontier(
    affectedParagraphIds: string[],
    documentParagraphs: Array<{id: string, graphemeStart: number, graphemeEnd: number, version: number}>,
    paragraphToPageMap: Map<string, number[]>,
    currentDocHash: string,
    pageCache: Map<string, PageLayout>
  ): { para_ix: number; line_ix: number } | null {
    if (affectedParagraphIds.length === 0) {
      DEBUG_ENABLED && console.warn('[SpillFrontierCalculator] No affected paragraphs for SpillFrontier calculation');
      return null;
    }

    // Find paragraph metadata for affected IDs
    const affectedMeta = mapParagraphIdsToIndices(affectedParagraphIds, documentParagraphs);

    if (affectedMeta.length === 0) {
      DEBUG_ENABLED && console.warn('[SpillFrontierCalculator] No paragraph metadata found for affected IDs:', affectedParagraphIds);
      // Fallback: start from beginning
      return { para_ix: 0, line_ix: 0 };
    }

    // Find minimum para_ix (first affected paragraph)
    const minParaIx = findMinParagraphIndex(affectedMeta) ?? 0;
    const firstAffectedId = findParagraphIdWithMinIndex(affectedMeta);

    if (!firstAffectedId) {
      return { para_ix: minParaIx, line_ix: 0 };
    }

    // Find which pages contain this paragraph
    const pageIndices = paragraphToPageMap.get(firstAffectedId) || [];

    if (pageIndices.length === 0) {
      // Paragraph not yet paginated, start from beginning
      DEBUG_ENABLED && console.log(`[SpillFrontierCalculator] Paragraph ${firstAffectedId} not in page map, using fallback`);
      return { para_ix: minParaIx, line_ix: 0 };
    }

    // Find first page containing this paragraph
    const firstPageIndex = findMinValueWithDefault(pageIndices, 0);
    const cacheKey = generatePageCacheKey(currentDocHash, firstPageIndex);
    const firstPage = pageCache.get(cacheKey);

    if (!firstPage) {
      // Page not in cache, use fallback
      return { para_ix: minParaIx, line_ix: 0 };
    }

    // Find paragraph slice in this page to get starting line
    const slice = findParagraphSliceInPage(firstPage, firstAffectedId);

    if (slice) {
      DEBUG_ENABLED && console.log(`📊 [SpillFrontierCalculator] SpillFrontier calculated:`, {
        affectedParagraph: firstAffectedId,
        para_ix: minParaIx,
        line_ix: slice.line_start,
        fromPage: firstPageIndex
      });

      return {
        para_ix: minParaIx,
        line_ix: slice.line_start
      };
    }

    // Fallback: start from beginning of paragraph
    return { para_ix: minParaIx, line_ix: 0 };
  }

  /**
   * 🆕 LINE-LEVEL OPTIMIZATION: Calculate SpillFrontier using edit position and cached snapshots
   *
   * This method provides more precise SpillFrontier calculation by using the edit position
   * to determine exactly which line was affected, rather than starting from line 0.
   *
   * Strategy:
   * 1. Find which paragraph contains the edit position
   * 2. Look up the cached snapshot for that paragraph
   * 3. Find which line in the snapshot contains the edit position
   * 4. Return SpillFrontier pointing to that specific line
   *
   * @param editPosition - Grapheme index where the edit occurred
   * @param affectedParagraphIds - Array of paragraph IDs that were modified
   * @param documentParagraphs - Array of paragraph metadata
   * @param cachedParagraphSnapshots - Cached paragraph snapshots with line data
   * @returns SpillFrontier cursor with precise line_ix, or null if calculation fails
   */
  static calculateSpillFrontierFromEditPosition(
    editPosition: number,
    affectedParagraphIds: string[],
    documentParagraphs: Array<{id: string, graphemeStart: number, graphemeEnd: number, version: number}>,
    cachedParagraphSnapshots: ParagraphSnapshot[]
  ): { para_ix: number; line_ix: number } | null {
    if (affectedParagraphIds.length === 0) {
      DEBUG_ENABLED && console.warn('[SpillFrontierCalculator] No affected paragraphs for line-level SpillFrontier');
      return null;
    }

    // Find paragraph metadata for affected IDs
    const affectedMeta = mapParagraphIdsToIndices(affectedParagraphIds, documentParagraphs);

    if (affectedMeta.length === 0) {
      DEBUG_ENABLED && console.warn('[SpillFrontierCalculator] No paragraph metadata found for affected IDs');
      return { para_ix: 0, line_ix: 0 };
    }

    // Find minimum para_ix (first affected paragraph)
    const minParaIx = findMinParagraphIndex(affectedMeta) ?? 0;

    // Find the paragraph that contains the edit position
    const affectedParagraph = documentParagraphs.find(p => 
      editPosition >= p.graphemeStart && editPosition <= p.graphemeEnd
    );

    if (!affectedParagraph) {
      DEBUG_ENABLED && console.log('[SpillFrontierCalculator] Edit position not in any paragraph, using para_ix 0');
      return { para_ix: minParaIx, line_ix: 0 };
    }

    // Find the cached snapshot for this paragraph
    // For single-paragraph documents, use the first snapshot
    // For multi-paragraph, we'd need to match by paragraph_id (future enhancement)
    const snapshot = cachedParagraphSnapshots[minParaIx] || cachedParagraphSnapshots[0];

    if (!snapshot || !snapshot.lines || snapshot.lines.length === 0) {
      DEBUG_ENABLED && console.log('[SpillFrontierCalculator] No cached snapshot with lines, using line_ix 0');
      return { para_ix: minParaIx, line_ix: 0 };
    }

    // Find which line contains the edit position
    // Convert document-global edit position to paragraph-local position
    const localEditPosition = editPosition - affectedParagraph.graphemeStart;

    let affectedLineIx = 0;
    for (let i = 0; i < snapshot.lines.length; i++) {
      const line = snapshot.lines[i];
      // Check if edit position falls within this line's grapheme range
      // Note: Line indices are paragraph-local in the snapshot
      if (localEditPosition >= line.start_grapheme_index && localEditPosition <= line.end_grapheme_index) {
        affectedLineIx = i;
        break;
      }
      // If edit is before this line, we've found the insertion point
      if (localEditPosition < line.start_grapheme_index) {
        affectedLineIx = Math.max(0, i - 1);
        break;
      }
      // If this is the last line and edit is after it, use this line
      if (i === snapshot.lines.length - 1) {
        affectedLineIx = i;
      }
    }

    PRODUCTION_LOGS_ENABLED && console.log(`📊 [SpillFrontierCalculator] Line-level SpillFrontier:`, {
      editPosition,
      localEditPosition,
      para_ix: minParaIx,
      line_ix: affectedLineIx,
      totalLines: snapshot.lines.length
    });

    return {
      para_ix: minParaIx,
      line_ix: affectedLineIx
    };
  }
}
